import { motion } from "motion/react";
import { useLocale, useTranslations } from "use-intl";
import { lt } from "@liftquest/shared/localized";
import { useEffect, useState } from "react";
import { Medal, Sparkles } from "lucide-react";
import type { FinishResult } from "./actions";
import { LevelBadge, XpBar } from "@/components/app/xp-bar";
import { Button } from "@/components/ui/button";
import { achievementIcon, levelFromXp } from "@liftquest/shared/gamification";

export function ResultScreen({ result, onDone }: { result: FinishResult; onDone: () => void }) {
  const t = useTranslations("finish");
  const ta = useTranslations("achievements");
  const locale = useLocale();
  const before = levelFromXp(result.totalXp - result.xp);
  const after = levelFromXp(result.totalXp);
  const [progress, setProgress] = useState(before.progress);
  const leveledUp = result.levelAfter > result.levelBefore;

  useEffect(() => {
    const id = setTimeout(() => setProgress(leveledUp ? 1 : after.progress), 400);
    const id2 = leveledUp ? setTimeout(() => setProgress(after.progress), 1300) : undefined;
    return () => {
      clearTimeout(id);
      if (id2) clearTimeout(id2);
    };
  }, [after.progress, leveledUp]);

  return (
    <div className="relative mx-auto flex min-h-dvh w-full max-w-md flex-col overflow-hidden px-5 pb-10 pt-safe">
      <Confetti />
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 220, damping: 14 }}
        className="mt-14 text-center"
      >
        <h1 className="text-3xl font-black tracking-tight">{t("doneTitle")}</h1>
        <p className="mt-4 bg-gradient-to-br from-xp to-[oklch(0.68_0.18_45)] bg-clip-text text-6xl font-black text-transparent">
          {t("xpEarned", { xp: result.xp })}
        </p>
      </motion.div>

      <div className="mt-8 flex items-center gap-3 rounded-2xl border bg-card p-4">
        <motion.div
          key={result.levelAfter}
          initial={leveledUp ? { rotate: -20, scale: 0.5 } : false}
          animate={{ rotate: 0, scale: 1 }}
          transition={{ delay: 1.2, type: "spring" }}
        >
          <LevelBadge level={leveledUp && progress === 1 ? result.levelBefore : result.levelAfter} />
        </motion.div>
        <div className="min-w-0 flex-1">
          <XpBar progress={progress} />
          <p className="mt-1.5 text-xs text-muted-foreground tabular-nums">
            {after.xpIntoLevel} / {after.xpForNext} XP
          </p>
        </div>
      </div>
      {leveledUp && (
        <motion.p
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="mt-3 flex items-center justify-center gap-2 rounded-2xl bg-xp/15 p-3 font-semibold text-xp-foreground dark:text-xp"
        >
          <Sparkles className="size-5" /> {t("levelUp", { level: result.levelAfter })}
        </motion.p>
      )}

      <ul className="mt-6 space-y-1.5">
        {result.breakdown.map((b, i) => (
          <motion.li
            key={b.key}
            initial={{ x: -12, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3 + i * 0.1 }}
            className="flex items-center justify-between rounded-xl bg-muted/60 px-3.5 py-2.5 text-sm"
          >
            <span>
              {t(`breakdown.${b.key}` as "breakdown.sets")}
              {b.count != null && <span className="text-muted-foreground"> × {b.count}</span>}
            </span>
            <span className="font-semibold tabular-nums text-xp-foreground dark:text-xp">+{b.xp}</span>
          </motion.li>
        ))}
      </ul>

      {result.records.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-2 flex items-center gap-2 font-semibold">
            <Medal className="size-5 text-xp" /> {t("newRecords")}
          </h2>
          <ul className="space-y-1.5">
            {result.records.map((r) => (
              <li key={lt(r.name, locale)} className="flex justify-between rounded-xl border px-3.5 py-2.5 text-sm">
                <span>{lt(r.name, locale)}</span>
                <span className="font-semibold tabular-nums">
                  {r.durationSec ? `${r.durationSec} s` : `${r.weight ?? 0} kg × ${r.reps}`}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {result.achievements.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-2 font-semibold">{t("newAchievements")}</h2>
          <div className="grid grid-cols-2 gap-2">
            {result.achievements.map((key, i) => (
              <motion.div
                key={key}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.8 + i * 0.15, type: "spring" }}
                className="rounded-2xl border border-xp/40 bg-xp/10 p-3 text-center"
              >
                <span className="text-3xl">{achievementIcon(key)}</span>
                <p className="mt-1 text-sm font-semibold">{ta(`${key}.title` as "first_workout.title")}</p>
                <p className="text-xs text-muted-foreground">{ta(`${key}.desc` as "first_workout.desc")}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      <p className="mt-6 text-center text-sm text-muted-foreground">{t("cooldown")}</p>
      <Button size="lg" className="mt-6 h-14 w-full rounded-2xl text-base" onClick={onDone}>
        {t("toDashboard")}
      </Button>
    </div>
  );
}

const COLORS = ["var(--primary)", "var(--xp)", "var(--chart-3)", "var(--chart-4)"];

function Confetti() {
  const [pieces] = useState(() =>
    Array.from({ length: 36 }, (_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.6,
      duration: 2.2 + Math.random() * 1.6,
      rotate: Math.random() * 720 - 360,
      color: COLORS[i % COLORS.length],
      size: 6 + Math.random() * 6,
    })),
  );
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((p, i) => (
        <motion.span
          key={i}
          className="absolute top-0 block rounded-sm"
          style={{ left: `${p.left}%`, width: p.size, height: p.size * 0.6, background: p.color }}
          initial={{ y: -20, opacity: 1, rotate: 0 }}
          animate={{ y: "100dvh", opacity: [1, 1, 0], rotate: p.rotate }}
          transition={{ delay: p.delay, duration: p.duration, ease: "easeIn" }}
        />
      ))}
    </div>
  );
}
